import { getLeadSession, setLeadSession } from './lead-session';

type LeadResponse = {
	data?: {
		errors?: Record<string, string>;
		message?: string;
		redirect?: string;
		token?: string;
	};
	success: boolean;
};

const showError = (el: HTMLElement | null, message: string) => {
	if (!el) return;
	el.textContent = message;
	el.classList.toggle('hidden', message === '');
};

const clearErrors = (form: HTMLFormElement) => {
	form
		.querySelectorAll<HTMLElement>('[data-error-for]')
		.forEach((el) => showError(el, ''));
	form
		.querySelectorAll('[aria-invalid]')
		.forEach((el) => el.removeAttribute('aria-invalid'));
	showError(form.querySelector('[data-form-error]'), '');
};

const fieldErrorMessage = (
	form: HTMLFormElement,
	field: string,
): string => {
	if (field === 'name') {
		const name =
			form.querySelector<HTMLInputElement>('[name="name"]')?.value.trim() ??
			'';
		return name === '' ? 'Informe seu nome.' : '';
	}
	if (field === 'email') {
		const email =
			form.querySelector<HTMLInputElement>('[name="email"]')?.value.trim() ??
			'';
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
			? ''
			: 'Informe um e-mail válido.';
	}
	if (field === 'phone') {
		const phone = (
			form.querySelector<HTMLInputElement>('[name="phone"]')?.value ?? ''
		).replace(/\D/g, '');
		return [10, 11].includes(phone.length)
			? ''
			: 'Informe um telefone válido com DDD.';
	}
	if (field === 'consent') {
		return form.querySelector<HTMLInputElement>('[name="consent"]')?.checked
			? ''
			: 'É necessário concordar para continuar.';
	}
	return '';
};

const syncFieldErrorIfShown = (form: HTMLFormElement, field: string) => {
	const errorEl = form.querySelector<HTMLElement>(`[data-error-for="${field}"]`);
	if (!errorEl || errorEl.classList.contains('hidden')) return;

	const message = fieldErrorMessage(form, field);
	showError(errorEl, message);
	const control = form.querySelector(`[name="${field}"]`);
	if (message === '') {
		control?.removeAttribute('aria-invalid');
	} else {
		control?.setAttribute('aria-invalid', 'true');
	}
};

export const initLeadForm = (): void => {
	const form = document.querySelector<HTMLFormElement>('[data-lead-form]');
	if (!form) return;

	const session = getLeadSession();
	const thankYouUrl = session?.thankYouUrl || form.dataset.thankYouUrl;
	if (session?.token && thankYouUrl) {
		window.location.replace(thankYouUrl);
		return;
	}

	form.addEventListener('input', (event) => {
		const target = event.target;
		if (!(target instanceof HTMLInputElement)) return;
		if (target.name === 'name' || target.name === 'email' || target.name === 'phone') {
			syncFieldErrorIfShown(form, target.name);
		}
	});

	form.addEventListener('change', (event) => {
		const target = event.target;
		if (target instanceof HTMLInputElement && target.name === 'consent') {
			syncFieldErrorIfShown(form, 'consent');
		}
	});

	form.addEventListener('submit', async (event) => {
		event.preventDefault();
		clearErrors(form);

		const button = form.querySelector<HTMLButtonElement>(
			'button[type="submit"]',
		);
		if (button) button.disabled = true;

		const body = new FormData(form);
		body.append('action', 'mlarlive_lead_submit');
		body.append('nonce', form.dataset.nonce ?? '');
		body.append('page_id', form.dataset.pageId ?? '');

		try {
			const response = await fetch(form.dataset.ajaxUrl ?? '', {
				body,
				method: 'POST',
			});
			const json = (await response.json()) as LeadResponse;

			if (json.success && json.data?.redirect && json.data.token) {
				setLeadSession({
					answers: {},
					thankYouUrl: json.data.redirect,
					token: json.data.token,
				});
				window.location.href = json.data.redirect;
				return;
			}

			const errors = json.data?.errors;
			if (errors) {
				Object.entries(errors).forEach(([field, message]) => {
					showError(form.querySelector(`[data-error-for="${field}"]`), message);
					form
						.querySelector(`[name="${field}"]`)
						?.setAttribute('aria-invalid', 'true');
				});
			} else {
				throw new Error('request failed');
			}
		} catch {
			showError(
				form.querySelector('[data-form-error]'),
				form.dataset.errorMessage ?? '',
			);
		}

		if (button) button.disabled = false;
	});
};
